import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  getAppearance,
  setAppearance as persistAppearance,
  type AppearancePreference,
} from '../services/settings';

interface ThemeContextValue {
  preference: AppearancePreference;
  setPreference: (pref: AppearancePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  preference: 'system',
  setPreference: () => {},
});

const systemDark = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const apply = (pref: AppearancePreference) => {
  const dark = pref === 'dark' || (pref === 'system' && systemDark());
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
};

export function ThemeProvider({children}: {children: ReactNode}) {
  const [preference, setPreferenceState] = useState<AppearancePreference>(
    getAppearance,
  );

  useEffect(() => {
    apply(preference);
    if (preference !== 'system') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((pref: AppearancePreference) => {
    persistAppearance(pref);
    setPreferenceState(pref);
  }, []);

  return (
    <ThemeContext.Provider value={{preference, setPreference}}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
